import LuxonUtils from '@date-io/luxon'

import {
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Checkbox as MCheckbox,
  RadioGroup as MRadioGroup,
  Slider as MSlider,
  Switch as MSwitch,
  MenuItem,
  Popper,
  Radio,
  Select,
  TextField,
  Theme,
  Typography,
  makeStyles,
} from '@material-ui/core'
import Icon from '@material-ui/core/Icon'
import { HighlightOff, Visibility, VisibilityOff } from '@material-ui/icons'
import Done from '@material-ui/icons/Done'
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete'
import {
  KeyboardDatePicker,
  KeyboardDateTimePicker,
  KeyboardTimePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { createStyles } from '@material-ui/styles'

import { ClipboardEvent, MutableRefObject, useEffect, useState } from 'react'
import shallow from 'zustand/shallow'
import { CustomDateRangeApi } from './data-range-selector'
import { dateFromInput, dateTimeLocal, past, pastMonths, pastWeeks, pastYears, dateMin } from './date-time'
import { Control, FormApi, ListProps, formApi } from './form'
import { withTooltip } from './format'
import useDebounce from './hooks/useDebounce'
import { darkBlue, lightGrayBorder, lightGreen, rootStyle } from './theme'
import { DateTime as DTime } from 'luxon'

type UIEvent = {
  target: { value: any; checked?: boolean }
  key?: string
  preventDefault?: () => void
}

function onValueChange(control: Control): (e: UIEvent) => void {
  const { onChange } = control.field
  return (e: UIEvent) => {
    const value = e.target.value
    if (onChange) {
      onChange(value)
    }
    control.value.getState().setValue(value)
  }
}

function onControlKeyUp(control: Control, allowDefault = false): (e: any) => void {
  const { onEnter, onKeyUp, onEnterValid } = control.field

  if (!onEnter && !onEnterValid && !onKeyUp) {
    return null
  }

  let enterCount = 0

  return (e: UIEvent) => {
    const value = e.target.value
    if (e.key === 'Enter') {
      enterCount++

      if (onEnter) {
        onEnter(value)
      }
      if (onEnterValid && enterCount % 2 === 0) {
        if (value) {
          enterCount = 0
          onEnterValid(value)
        }
      }

      // console.log('on enter', onEnterValid, enterCount, allowDefault, value)
      if (!allowDefault) {
        e.preventDefault()
      }
    } else if (onKeyUp) {
      onKeyUp(value)
    }
  }
}

function defaultOnPaste(e: ClipboardEvent<HTMLInputElement>) {
  e.preventDefault()
  let str = e.clipboardData.getData('text/plain')
  console.info('paste len before', str.length)
  str = str.replace(/[^\x20-\x7E]/g, '')
  console.info('paste len after', str.length)
  window.document.execCommand('insertText', false, str)
}

export function Text(c: Control) {
  const {
    startIcon,
    tooltip,
    inputRef,
    required,
    style,
    multiline,
    rows,
    autoFocus,
    helperText,
    clearIcon,
    placeholder,
    label,
    name,
    onClear,
  } = c.field
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)
  const disabled = c.disabled((s) => s.value)
  const classes = rootStyle(style || { width: '100%' })()

  const onClearClicked = () => {
    setValue('')
    if (onClear) {
      onClear()
    }
  }

  const id = c.id || c.field.id
  return withTooltip(
    tooltip,
    <TextField
      onChange={onValueChange(c)}
      onKeyUp={onControlKeyUp(c)}
      className={classes.root}
      required={required}
      error={!!error}
      multiline={multiline}
      rows={rows}
      autoFocus={autoFocus}
      helperText={helperText}
      placeholder={placeholder}
      label={label}
      disabled={disabled}
      style={style}
      name={name}
      id={id ? id : 'text_' + (label ? label?.toLowerCase()?.trim() : id)}
      InputProps={{
        ref: inputRef,
        id: label,
        onPaste: defaultOnPaste,
        value: value || '',
        startAdornment: startIcon ? (
          <InputAdornment position="start">
            {' '}
            <Icon>{startIcon}</Icon>
          </InputAdornment>
        ) : null,
        endAdornment: clearIcon ? (
          <InputAdornment position="end">
            <IconButton onClick={onClearClicked}>
              <HighlightOff />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />,
  )
}

export function PasswordText(c: Control) {
  const { tooltip, inputRef, required, style, autoFocus, helperText, placeholder, label, togglePassword, name } =
    c.field
  const [value, error] = c.value((s) => [s.value, s.error], shallow)
  const disabled = c.disabled((s) => s.value)

  const [showPassword, setShowPassword] = useState(false)

  const classes = rootStyle(style || { width: '100%' })()
  const id = c.id || c.field.id
  return withTooltip(
    tooltip,
    <TextField
      onChange={onValueChange(c)}
      onKeyUp={onControlKeyUp(c)}
      className={classes.root}
      type={showPassword ? 'text' : 'password'}
      required={required}
      error={!!error}
      autoFocus={autoFocus}
      helperText={helperText}
      placeholder={placeholder}
      label={label}
      style={style}
      name={name}
      disabled={disabled}
      id={id ? id : 'password_' + (label ? label?.toLowerCase()?.trim() : id)}
      InputProps={{
        ref: inputRef,
        value: value || '',
        endAdornment: togglePassword ? (
          <InputAdornment position="end">
            <IconButton onClick={() => setShowPassword(!showPassword)} onMouseDown={(e) => e.preventDefault()}>
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />,
  )
}

export function NumberText(c: Control) {
  let { min, max, tooltip, inputRef, required, style, autoFocus, helperText, placeholder, label, name } = c.field
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)
  const disabled = c.disabled((s) => s.value)

  if (!tooltip) {
    tooltip = `Min: ${min} Max: ${max}`
  }

  function onInput(e: any) {
    if (!e) {
      return
    }
    const n = Math.max(min, Math.min(max, parseInt(e.target.value, 10)))
    if (c.field.onChange) {
      c.field.onChange(n)
    }
    setValue(n)
  }
  const id = c.id || c.field.id
  return withTooltip(
    tooltip,
    <TextField
      onChange={onInput}
      onKeyUp={onControlKeyUp(c, true)}
      autoFocus={autoFocus}
      helperText={helperText}
      placeholder={placeholder}
      label={label}
      style={style}
      required={required}
      error={!!error}
      name={name}
      id={id ? id : 'number_' + (label ? label?.toLowerCase()?.trim() : id)}
      disabled={disabled}
      inputProps={{
        type: 'number',
        min,
        max,
        ref: inputRef,
        value: isNaN(value) ? '' : value,
      }}
    />,
  )
}

export function Auto(c: Control) {
  const {
    tooltip,
    inputRef,
    required,
    style,
    placeholder,
    label,
    name,
    disableClearable,
    listLabel,
    equals,
    optionDisabled,
    autoFocus,
    searchWidth,
    searchHeight,
  } = c.field as ListProps

  const [value, setValue, error] = c.value((s) => [s.value || s.defaultValue, s.setValue, s.error], shallow)
  const disabled = c.disabled((s) => s.value)

  const onChange = (_: any, v: any) => {
    if (c.field.onChange) {
      c.field.onChange(v)
    }
    setValue(v)
  }

  if (!c.list) {
    throw new Error('list required for auto component: ' + label)
  }

  const list = c.list((s) => s.value)

  const getOptionLabel = (item: any) => {
    if (!item) {
      return ''
    }
    const label = listLabel(item)
    return label || ''
  }

  const getOptionSelected = equals || ((option, value) => (value === '' ? true : option === value))

  const id = c.id || c.field.id

  return withTooltip(
    tooltip,
    <Autocomplete
      disabled={disabled}
      disableClearable={disableClearable}
      options={list}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      getOptionDisabled={optionDisabled}
      onChange={onChange}
      style={{ ...{ width: '100%' }, ...style }}
      value={value || ''}
      id={id ? id : 'single_select_' + (label ? label?.toLowerCase()?.trim() : id)}
      ListboxProps={{
        style: {
          maxHeight: searchHeight,
        },
      }}
      PopperComponent={searchWidth ? SearchPopper(searchWidth) : undefined}
      renderInput={(params: any) => (
        <TextField
          {...params}
          variant={c.variant}
          ref={inputRef}
          label={label}
          name={name}
          disabled={disabled}
          onKeyUp={onControlKeyUp(c, true)}
          autoFocus={autoFocus}
          placeholder={placeholder}
          required={required}
          error={!!error}
          style={{ width: '100%' }}
          size={'small'}
        />
      )}
    />,
  )
}

const SearchPopper = (width: string | number) => (props) => {
  return <Popper {...props} style={{ width }} popperOptions={{}} placement="bottom-start" />
}

export function getAutoCompleteInput(
  inputRef?: MutableRefObject<HTMLElement | HTMLInputElement>,
): () => HTMLInputElement | null {
  let inputEl: HTMLInputElement
  return () => {
    if (inputEl) {
      return inputEl
    }
    const el: HTMLInputElement = inputRef?.current as any
    if (el && el.children) {
      inputEl = el.children[1].children[0] as any
      return inputEl
    }
    return null
  }
}

export function inputValueSelection(fn: () => HTMLInputElement | null): (value?: string) => void {
  return (value) => {
    const el = fn()
    if (el) {
      el.value = value || el.value || ''
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
    }
  }
}

let searchOpen = false
export function Search(c: Control) {
  const props: ListProps = c.field as ListProps
  const {
    tooltip,
    inputRef,
    required,
    style,
    placeholder,
    label,
    name,
    disableClearable,
    listLabel,
    listValue,
    textLabel,
    onEnter,
    onEnterValid,
  } = props
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)
  const disabled = c.disabled((s) => s.value)

  const onChange = (_: any, v: any) => {
    searchOpen = false
    if (listValue) {
      setValue(listValue(v))
    } else {
      setValue(v)
    }
  }

  if (!c.list) {
    throw new Error('list required for search component: ' + label)
  }

  const list = c.list((s) => s.value)

  const onKeyUp = (e: any) => {
    const v = e.target.value
    if ((v || '').length < 3) {
      searchOpen = false
    }

    if (searchOpen) {
      return
    }

    if (e.key === 'Enter') {
      searchOpen = false
      if (onEnter) {
        onEnter(v)
      }
      if (onEnterValid) {
        if (value) {
          onEnterValid(value)
        }
      }
    } else {
      if (c.field.onKeyUp) {
        c.field.onKeyUp(v)
      }
    }
  }

  const onKeyDown = c.field.onKeyDown
    ? (e: any) => {
        const v = e.target.value
        c.field.onKeyDown(v)
      }
    : undefined

  const getOptionLabel = (item: any, isList = false) => {
    if (!item) {
      searchOpen = false
      return ''
    }
    searchOpen = isList
    const label = isList ? listLabel(item) : textLabel ? textLabel(item) : listLabel(item)
    return label || ''
  }

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    ignoreAccents: true,
    stringify: (item: any) => {
      return getOptionLabel(item, true)
    },
  })

  const onInputChange = props.onInputChange
    ? (e: any) => {
        const v = e.target.value
        props.onInputChange(v, e)
      }
    : undefined

  const onClose = (e: any) => {
    searchOpen = false

    const v = c.value.getState()

    if (v?.value) {
      onChange(null, { ...v.value })
    }

    if (props.onClose) {
      props.onClose()
    }
  }
  const id = c.id || c.field.id
  return withTooltip(
    tooltip,
    <Autocomplete
      freeSolo={true}
      autoSelect={false}
      filterOptions={filterOptions}
      disableClearable={disableClearable}
      options={list}
      openOnFocus={true}
      getOptionLabel={getOptionLabel}
      filterSelectedOptions
      onChange={onChange}
      onClose={onClose}
      disabled={disabled}
      style={{ ...{ width: '100%' }, ...style }}
      value={value || ''}
      id={id ? id : 'search_' + (label ? label?.toLowerCase()?.trim() : id)}
      PopperComponent={SearchPopper('600px')}
      renderInput={(params: any) => (
        <TextField
          {...params}
          ref={inputRef}
          label={label}
          name={name}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onChange={onInputChange}
          placeholder={placeholder}
          required={required}
          error={!!error}
          style={{ width: '100%' }}
          data-field={label}
        />
      )}
    />,
  )
}



let asyncSearchOpen = false
export function AsyncSearch(c: Control) {
  const props: ListProps = c.field as ListProps
  const {
    tooltip,
    inputRef,
    required,
    style,
    placeholder,
    label,
    name,
    disableClearable,
    asyncList,
    listLabel,
    listValue,
    textLabel,
    onEnter,
    onEnterValid,
  } = props
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)
  const disabled = c.disabled((s) => s.value)

  const onChange = (_: any, v: any) => {
    asyncSearchOpen = false
    if (listValue) {
      setValue(listValue(v))
    } else {
      setValue(v)
    }
  }

  if (!asyncList) {
    throw new Error('asyncList required for search component: ' + label)
  }

  const [options, setOptions] = useState([])
  const [input, setInput] = useState('')

  useEffect(() => {
    let active = true 
    ;(async () => {
      const list = await asyncList(input)

      if (active) {
        setOptions(list || [])
      }
    })()

    return () => {
      active = false
    }
  }, [input])

  useEffect(() => {
    if (!asyncSearchOpen) {
      setOptions([])
    }
  }, [asyncSearchOpen])

  const onKeyUp = (e: any) => {
    const v = e.target.value
    if ((v || '').length < 3) {
      asyncSearchOpen = false
    }

    if (asyncSearchOpen) {
      return
    }

    if (e.key === 'Enter') {
      asyncSearchOpen = false
      if (onEnter) {
        onEnter(v)
      }
      if (onEnterValid) {
        if (value) {
          onEnterValid(value)
        }
      }
    } else {
      if (c.field.onKeyUp) {
        c.field.onKeyUp(v)
      }
    }
  }

  const onKeyDown = c.field.onKeyDown
    ? (e: any) => {
        const v = e.target.value
        c.field.onKeyDown(v)
      }
    : undefined

  const getOptionLabel = (item: any, isList = false) => {
    if (!item) {
      searchOpen = false
      return ''
    }
    searchOpen = isList
    const label = isList ? listLabel(item) : textLabel ? textLabel(item) : listLabel(item)
    return label || ''
  }

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    ignoreAccents: true,
    stringify: (item: any) => {
      return getOptionLabel(item, true)
    },
  })

  const onInputChange = (e: any) => {
    const v = e.target.value
    setInput(v)
    if (props.onInputChange) {
      props.onInputChange(v, e)
    }
  }

  const onClose = (e: any) => {
    searchOpen = false

    const v = c.value.getState()

    if (v?.value) {
      onChange(null, { ...v.value })
    }

    if (props.onClose) {
      props.onClose()
    }
  }
  const id = c.id || c.field.id
  return withTooltip(
    tooltip,
    <Autocomplete
      freeSolo={true}
      autoSelect={false}
      filterOptions={filterOptions}
      disableClearable={disableClearable}
      options={options}
      openOnFocus={true}
      getOptionLabel={getOptionLabel}
      filterSelectedOptions
      onChange={onChange}
      onClose={onClose}
      disabled={disabled}
      style={{ ...{ width: '100%' }, ...style }}
      value={value || ''}
      id={id ? id : 'search_' + (label ? label?.toLowerCase()?.trim() : id)}
      PopperComponent={SearchPopper('600px')}
      renderInput={(params: any) => (
        <TextField
          {...params}
          ref={inputRef}
          label={label}
          name={name}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onChange={onInputChange}
          placeholder={placeholder}
          required={required}
          error={!!error}
          style={{ width: '100%' }}
          data-field={label}
        />
      )}
    />,
  )
}

export function Tags(c: Control) {
  const {
    tooltip,
    inputRef,
    required,
    style,
    placeholder,
    label,
    name,
    disableClearable,
    listLabel,
    equals,
    autoFocus,
    searchWidth,
  } = c.field as ListProps

  const { variant } = c
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)
  const disabled = c.disabled((s) => s.value)

  const onChange = (_: any, v: any) => {
    if (c.field.onChange) {
      c.field.onChange(v)
    }
    setValue(v)
  }

  if (!c.list) {
    throw new Error('list required for tags component: ' + label)
  }

  const list = c.list((s) => s.value)

  const getOptionLabel = (item: any) => {
    if (!item) {
      return ''
    }
    const label = listLabel(item)
    return label || ''
  }

  const getOptionSelected = equals || ((option, value) => (value === '' ? true : option === value))

  const id = c.id || c.field.id

  return withTooltip(
    tooltip,
    <Autocomplete
      multiple
      id={id ? id : 'tags_' + (label ? label?.toLowerCase()?.trim() : id)}
      size="small"
      disableClearable={disableClearable}
      options={list}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      onChange={onChange}
      disabled={disabled}
      style={
        typeof variant === 'undefined'
          ? { ...{ width: '100%', maxHeight: 24 }, ...style }
          : { ...{ width: '100%' }, ...style }
      }
      ref={inputRef}
      value={value}
      PopperComponent={searchWidth ? SearchPopper(searchWidth) : undefined}
      renderInput={(params: any) => (
        <TextField
          {...params}
          label={typeof variant === 'undefined' && label}
          name={name}
          variant={variant}
          onKeyUp={onControlKeyUp(c, true)}
          autoFocus={autoFocus}
          placeholder={placeholder}
          required={required}
          error={!!error}
          style={{ width: '100%' }}
          data-field={label}
        />
      )}
    />,
  )
}

export function TruncatedTags(c: Control) {
  const {
    tooltip,
    inputRef,
    required,
    style,
    placeholder,
    label,
    name,
    disableClearable,
    listLabel,
    equals,
    autoFocus,
    searchWidth,
    optionWidth,
    optionHeight,
  } = c.field as ListProps

  const { variant } = c
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)
  const disabled = c.disabled((s) => s.value)

  const onChange = (_: any, v: any) => {
    if (c.field.onChange) {
      c.field.onChange(v)
    }
    setValue(v)
  }

  if (!c.list) {
    throw new Error('list required for tags component: ' + label)
  }

  const list = c.list((s) => s.value)

  const getOptionLabel = (item: any) => {
    if (!item) {
      return ''
    }
    const label = listLabel(item)
    return label || ''
  }

  const getOptionSelected = equals || ((option, value) => (value === '' ? true : option === value))

  const id = c.id || c.field.id

  return withTooltip(
    tooltip,
    <Autocomplete
      multiple
      id={id}
      size="small"
      disableClearable={disableClearable}
      options={list}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      onChange={onChange}
      disabled={disabled}
      style={{ ...{ width: '100%' }, ...style }}
      ref={inputRef}
      value={value}
      PopperComponent={searchWidth ? SearchPopper(searchWidth) : undefined}
      renderTags={(value, getTagProps) =>
        value.map((option, index) =>
          withTooltip(
            getOptionLabel(option),
            <Chip
              size="small"
              variant="outlined"
              style={{
                margin: 4,
                backgroundColor: option.selected ? lightGreen : undefined,
              }}
              label={
                <Typography
                  style={{
                    fontSize: 11,
                    width: `${optionWidth || 100}px`,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {getOptionLabel(option)}
                </Typography>
              }
              {...getTagProps({ index })}
            />,
          ),
        )
      }
      renderInput={(params) => {
        const { InputProps, ...restParams } = params
        const { startAdornment, ...restInputProps } = InputProps
        return (
          <TextField
            {...restParams}
            label={typeof variant === 'undefined' && label}
            name={name}
            variant={variant}
            onKeyUp={onControlKeyUp(c, true)}
            autoFocus={autoFocus}
            placeholder={placeholder}
            required={required}
            error={!!error}
            style={{ width: '100%' }}
            data-field={label}
            InputProps={{
              ...restInputProps,
              startAdornment: (
                <div
                  style={{
                    maxHeight: optionHeight || 80,
                    overflowY: 'auto',
                  }}
                >
                  {startAdornment}
                </div>
              ),
            }}
          />
        )
      }}
    />,
  )
}

export function TagsAuditReport(c: Control) {
  const {
    tooltip,
    inputRef,
    required,
    style,
    placeholder,
    label,
    name,
    disableClearable,
    listLabel,
    autoFocus,
    searchWidth,
  } = c.field as ListProps

  const { variant } = c
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)
  const disabled = c.disabled((s) => s.value)

  const onChange = (_: any, v: any) => {
    if (c.field.onChange) {
      c.field.onChange(v)
    }
    setValue(v)
  }

  if (!c.list) {
    throw new Error('list required for tags component: ' + label)
  }

  const list = c.list((s) => s.value)

  const getOptionLabel = (item: any) => {
    if (!item) {
      return ''
    }
    const label = listLabel(item)
    return label || ''
  }

  const id = c.id || c.field.id

  return withTooltip(
    tooltip,
    <Autocomplete
      multiple
      id={id ? id : 'tags_audit_' + (label ? label?.toLowerCase()?.trim() : id)}
      size="small"
      disableClearable={disableClearable}
      options={list}
      getOptionLabel={getOptionLabel}
      getOptionSelected={(option, value) => option.name == value.name}
      onChange={onChange}
      disabled={disabled}
      style={
        typeof variant === 'undefined'
          ? { ...{ width: '100%', maxHeight: 24 }, ...style }
          : { ...{ width: '100%' }, ...style }
      }
      ref={inputRef}
      value={value}
      PopperComponent={searchWidth ? SearchPopper(searchWidth) : undefined}
      renderInput={(params: any) => (
        <TextField
          {...params}
          label={typeof variant === 'undefined' && label}
          name={name}
          variant={variant}
          onKeyUp={onControlKeyUp(c, true)}
          autoFocus={autoFocus}
          placeholder={placeholder}
          required={required}
          error={!!error}
          style={{ width: '100%' }}
          data-field={label}
        />
      )}
    />,
  )
}

function onBooleanValueChange(ctl: Control): (e: UIEvent) => void {
  const { selected, value, field } = ctl
  const { onChange } = ctl.field

  return (e: UIEvent) => {
    const v = e.target.checked

    if (field?.useBooleanValue) {
      value.getState().setValue(v)
    } else {
      value.getState().setValue(e.target.value)
    }

    selected.getState().setValue(v)

    if (onChange) {
      onChange(v)
    }
  }
}

export function Switch(c: Control) {
  const { label, tooltip, name, defaultValue, style } = c.field
  const selected = c.selected((s) => s.value)
  const disabled = c.disabled((s) => s.value)

  const id = c.id || c.field.id
  return withTooltip(
    tooltip,
    <FormControlLabel
      label={label}
      key={name}
      control={
        <MSwitch
          id={id}
          disabled={disabled}
          checked={selected}
          value={defaultValue || ''}
          name={name}
          style={style}
          onChange={onBooleanValueChange(c)}
        />
      }
    />,
  )
}

export function CheckBox(c: Control) {
  const { label, tooltip, name, defaultValue, style, labelPlacement, onEnter, tabIndex } = c.field
  const selected = c.selected((s) => s.value)
  const disabled = c.disabled((s) => s.value)

  const onKeyUp = (e: any) => {
    const v = e.target.value
    if (e.key === 'Enter') {
      if (onEnter) {
        onEnter(v)
      }
    }
  }
  const id = c.id || c.field.id
  return withTooltip(
    tooltip,
    <FormControlLabel
      style={{ ...style, ...{ whiteSpace: 'nowrap' } }}
      label={label}
      labelPlacement={labelPlacement}
      key={name}
      control={
        <MCheckbox
          id={id ? id : 'checkbox_' + (label ? label?.toLowerCase()?.trim() : id)}
          tabIndex={tabIndex}
          onKeyUp={onKeyUp}
          disabled={disabled}
          checked={selected}
          value={defaultValue || ''}
          name={name}
          style={style}
          onChange={onBooleanValueChange(c)}
        />
      }
    />,
  )
}

export function RadioGroup(c: Control) {
  const { style, tooltip, row, small } = c.field
  const value = c.value((s) => s.value)

  const options = c.options((s) => s.value)

  return withTooltip(
    tooltip,
    <MRadioGroup row={row} style={style} value={value} onChange={onValueChange(c)}>
      {options.map(({ label, value: optionValue, disabled, hidden }, i) => (
        <FormControlLabel
          key={i.toString()}
          label={label || optionValue}
          value={optionValue}
          style={{ visibility: hidden ? 'hidden' : undefined }}
          control={
            <Radio
              id={'EM_RADIO_' + label}
              style={{ color: 'white' }}
              disabled={disabled}
              size={small ? 'small' : 'medium'}
            />
          }
        />
      ))}
    </MRadioGroup>,
  )
}

export function PastDateInline(c: Control) {
  const { label, tooltip, name, style, required, minDate, maxDate, autoFocus } = c.field
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)

  const disabled = c.disabled((s) => s.value)

  const onChange = (dt: any) => {
    if (!dt?.toJSDate) {
      return
    }
    const d = dt.toJSDate()
    console.log('inline onChange', d, d instanceof Date)
    if (d instanceof Date) {
      setValue(new Date(d))

      if (c.field.onChange) {
        c.field.onChange(d)
      }
    }
  }
  const classes = rootStyle({
    ...{ marginTop: '0px' },
    ...(style || { width: '100%' }),
  })()
  const id = c.id || c.field.id

  return withTooltip(
    tooltip,
    <MuiPickersUtilsProvider utils={LuxonUtils}>
      <KeyboardDatePicker
        key={name}
        autoOk={true}
        className={classes.root}
        required={required}
        autoFocus={autoFocus}
        error={!!error}
        disableToolbar
        variant="inline"
        format="MM/dd/yyyy"
        margin="none"
        label={label}
        name={name}
        id={id}
        value={value}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        onChange={onChange as any}
        onKeyUp={onControlKeyUp(c)}
        disableFuture
        KeyboardButtonProps={{
          'aria-label': 'change date',
        }}
      />
    </MuiPickersUtilsProvider>,
  )
}

export function DateInline(c: Control) {
  const { label, tooltip, name, style, required, minDate, maxDate, autoFocus, inputProps } = c.field
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)

  const disabled = c.disabled((s) => s.value)

  const onChange = (dt: Date | DTime) => {
    let d: Date

    if (dt instanceof Date) {
      d = new Date(dt)
    } else if (dt?.toJSDate) {
      d = new Date(dt.toJSDate())
    }

    if (minDate) {
      if (d < minDate) {
        d = minDate
      }
    }

    if (maxDate) {
      if (d > maxDate) {
        d = maxDate
      }
    }

    if (d instanceof Date) {
      if (c.field.onChange) {
        c.field.onChange(d)
      }
      setValue(d)
    }
  }
  const classes = rootStyle({
    ...{ marginTop: 0, maxHeight: 12 },
    ...(style || { width: '100%' }),
  })()
  const id = c.id || c.field.id

  return withTooltip(
    tooltip,
    <MuiPickersUtilsProvider utils={LuxonUtils}>
      <KeyboardDatePicker
        key={name}
        autoOk={true}
        className={classes.root}
        required={required}
        autoFocus={autoFocus}
        error={!!error}
        disableToolbar
        variant="inline"
        format="MM/dd/yyyy"
        margin="none"
        label={label}
        name={name}
        id={id}
        value={value}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        onChange={onChange as any}
        onKeyUp={onControlKeyUp(c)}
        InputProps={inputProps}
        KeyboardButtonProps={{
          'aria-label': 'change date',
        }}
      />
    </MuiPickersUtilsProvider>,
  )
}

export function Time(c: Control) {
  const { label, tooltip, name, style, required } = c.field
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)

  const disabled = c.disabled((s) => s.value)

  const onChange = (d: Date) => {
    setValue(d)
    if (c.field.onChange) {
      c.field.onChange(d)
    }
  }
  const classes = rootStyle({
    ...{ marginTop: '0px' },
    ...(style || { width: '100%' }),
  })()
  return withTooltip(
    tooltip,
    <MuiPickersUtilsProvider utils={LuxonUtils}>
      <KeyboardTimePicker
        label={label}
        required={required}
        name={name}
        error={!!error}
        disabled={disabled}
        placeholder="08:00 AM"
        mask="__:__ _M"
        value={value}
        onChange={(date) => onChange(date as any)}
      />
    </MuiPickersUtilsProvider>,
  )
}

export function DateTime(c: Control) {
  const { label, tooltip, name, style, required, minDate, maxDate, autoFocus } = c.field
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)

  const disabled = c.disabled((s) => s.value)

  const onChange = (e: any) => {
    const d: string = e.target.value
    const v = d && d.length ? new Date(d) : null

    if (c.field.onChange) {
      c.field.onChange(v)
    }

    setValue(v)
  }

  const id = c.id || c.field.id
  const sty = { ...{ width: 194 }, ...style }
  return withTooltip(
    tooltip,
    <TextField
      type="datetime-local"
      value={value ? dateTimeLocal(value) : ''}
      onChange={(e) => onChange(e)}
      onKeyUp={onControlKeyUp(c)}
      required={required}
      error={!!error}
      autoFocus={autoFocus}
      label={label}
      style={sty}
      name={name}
      id={id ? id : 'datetime_' + (label ? label?.toLowerCase()?.trim() : id)}
      disabled={disabled}
      InputProps={{
        inputProps: {
          min: minDate ? dateTimeLocal(minDate) : undefined,
          max: maxDate ? dateTimeLocal(maxDate) : undefined,
        },
      }}
      InputLabelProps={{
        shrink: true,
      }}
    />,
  )
}

export function DateTimeInline(c: Control) {
  const { label, tooltip, style, required, minDate, maxDate, autoFocus } = c.field
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)
  const disabled = c.disabled((s) => s.value)

  const onChange = (dt: any) => {
    if (!dt?.toJSDate) {
      return
    }
    const d = dt.toJSDate()
    //  console.log('inline onChange', d, d instanceof Date)
    if (d instanceof Date) {
      setValue(new Date(d))

      if (c.field.onChange) {
        c.field.onChange(d)
      }
    }
  }

  const classes = rootStyle({
    ...{ marginTop: '0px' },
    ...(style || { width: '100%' }),
  })()
  return withTooltip(
    tooltip,
    <MuiPickersUtilsProvider utils={LuxonUtils}>
      <KeyboardDateTimePicker
        variant="inline"
        error={!!error}
        autoOk={true}
        required={required}
        autoFocus={autoFocus}
        className={classes.root}
        onKeyUp={onControlKeyUp(c, true)}
        onChange={onChange as any}
        onError={console.log}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        ampm={false}
        label={label}
        value={value}
        disableFuture
        format="MM/dd/yyyy HH:mm"
      />
    </MuiPickersUtilsProvider>,
  )
}

const chipListStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      'width': '100%',
      'display': 'flex',
      'justifyContent': 'flex-start',
      'flexWrap': 'wrap',
      '& > *': {
        margin: theme.spacing(0.5),
      },
    },
  }),
)

export function ChipList(c: Control) {
  let { options, name, onChange, multipleSelect } = c.field

  const setValue = c.value((s) => s.setValue)
  const classes = chipListStyles()

  const [icon, setIcon] = useState(false)

  const onClick = (i: number) => {
    options[i].selected = !options[i].selected

    if (!multipleSelect) {
      options.forEach((o, j) => {
        if (j !== i) {
          o.selected = false
        }
      })
      setValue(options[i].selected ? options[i].value : null)
    }

    if (onChange) {
      onChange(options[i])
    }
    setIcon(!icon)
  }
  const active = c.disabled((s) => s.value)
  const id = c.id || c.field.id

  c.options((s) => s.value)

  return (
    <div key={name} className={classes.root}>
      {options.map((o, i) => (
        <Chip
          disabled={active}
          clickable
          key={i}
          id={id ? id : 'chip_' + o?.value}
          size="small"
          color="primary"
          style={
            o.selected
              ? { fontSize: 10, margin: 1, backgroundColor: darkBlue, color: 'white' }
              : { fontSize: 10, margin: 1, backgroundColor: lightGrayBorder, color: '#111' }
          }
          icon={o.selected ? <Done htmlColor="white" /> : null}
          label={o.label || o.value}
          onClick={() => onClick(i)}
        />
      ))}
    </div>
  )
}

export function options(opt: any) {
  if (!opt.options) {
    return null
  }
  const list = opt.noEmptyOption
    ? []
    : [
        <MenuItem value={-1} key="placeholder">
          {' '}
          {opt.placeholder ? opt.placeholder : <span>Select</span>}
        </MenuItem>,
      ]
  return list.concat(
    opt.options.map((o, i) => (
      <MenuItem key={i.toString()} value={i}>
        {o.label ? o.label : o.value}
      </MenuItem>
    )),
  )
}

// TODO convert forms to Auto
export function OptionList(c: Control) {
  let { label, tooltip, name, required, error, style } = c.field
  const value = c.value((s) => s.value)
  const classes = rootStyle(style || { width: '100%' })()
  return withTooltip(
    tooltip,
    <FormControl key={name} className={classes.root} required={required}>
      {label && label.length ? <InputLabel>{label}</InputLabel> : null}
      <Select error={!!error} value={isNaN(value) ? '' : value} onChange={onValueChange(c)}>
        {options(c.field)}
      </Select>
    </FormControl>,
  )
}

export function RangePicker(props: { c: Control; customDateRange: CustomDateRangeApi }) {
  const { c, customDateRange } = props
  const {
    tooltip,
    inputRef,
    required,
    style,
    placeholder,
    label,
    name,
    disableClearable,
    onClose,
    listLabel,
    equals,
    autoFocus,
    searchWidth,
    searchHeight,
    onEnterValid: onEnterValid,
  } = c.field as ListProps
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)
  const disabled = c.disabled((s) => s.value)
  const [number, setNumber] = useState(splitNumber(value?.label)) //number of the days, weeks, months and years
  const [filterType, setFilterType] = useState({
    label: splitFilterType(value?.label),
    range: { startDate: null, endDate: null },
  }) //type for search (days, weeks, months or years)
  const [endDate, setEndDate] = useState(null)

  const debouncedNumber = useDebounce<number>(number, 500)

  function splitNumber(value: string): number {
    if (value) {
      const arrTemp = value.split(' ')
      return parseFloat(arrTemp[0])
    }
    return 30
  }

  function splitFilterType(value: string): string {
    if (value) {
      const arrTemp = value.split(' ')
      return arrTemp.slice(1).join(' ')
    }
    return 'Days'
  }

  useEffect(() => {
    if (number.toString() != '' && filterType.label != 'Custom Range') {
      let y = findRange(filterType.label, number)
      setValue({
        label: number + ' ' + filterType.label,
        range: { startDate: y, endDate: new Date() },
      })

      c.field.onChange({
        label: number + ' ' + filterType.label,
        range: { startDate: y, endDate: new Date() },
      })
    }
  }, [debouncedNumber])

  const findRange = (labelRange: string, range: number): Date => {
    let tempDate: Date

    if (labelRange === 'Days') {
      tempDate = past(new Date(), range)
    } else if (labelRange === 'Weeks') {
      tempDate = pastWeeks(new Date(), range)
    } else if (labelRange === 'Months') {
      tempDate = pastMonths(new Date(), range)
    } else {
      tempDate = pastYears(new Date(), range)
    }

    return dateFromInput(tempDate)
  }

  const onChange = (_: any, v: any) => {
    v &&
      setFilterType((prevState) => {
        return {
          label: v?.label,
          range: {
            startDate: prevState.range.startDate,
            endDate: prevState.range.endDate,
          },
        }
      })
    if (v.label != 'Custom Range') {
      let y = splitNumber(value?.label) ? findRange(v.label, splitNumber(value?.label)) : null
      setValue({
        label: splitNumber(value?.label) + ' ' + v.label,
        range: { startDate: y, endDate: new Date() },
      })
      c.field.onChange({
        label: splitNumber(value?.label) + ' ' + v.label,
        range: { startDate: y, endDate: new Date() },
      })
    } else {
      let y = splitNumber(value?.label) ? findRange(v.label, splitNumber(value?.label)) : null
      setValue({
        label: splitNumber(value?.label) + ' ' + v.label,
        range: { startDate: y, endDate: new Date() },
      })
      customDateRange.data.setState({ open: true })
    }
  }

  const onChangeNumber = (input: any) => {
    const reg = /^\d+$/
    if (reg.test(input) || input === '') {
      setNumber(input)
    }
  }

  const getOptionLabel = (item: any) => {
    if (!item) {
      return ''
    }
    const label = listLabel(item)
    return label || ''
  }

  const getOptionSelected = equals || ((option, value) => (value === '' ? true : option === value))

  if (!c.list) {
    throw new Error('list required for auto component: ' + label)
  }

  let list = [
    { label: 'Days', startDate: null, endDate: endDate },
    { label: 'Weeks', startDate: null, endDate: endDate },
    { label: 'Months', startDate: null, endDate: null },
    { label: 'Years', startDate: null, endDate: endDate },
    { label: 'Custom Range', startDate: null, endDate: null },
  ]

  return withTooltip(
    tooltip,
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <div style={{ flex: 0.5, paddingLeft: 5 }}>Last</div>
      <TextField
        variant={'outlined'}
        label={''}
        value={number}
        disabled={splitFilterType(value?.label) === 'Custom Range'}
        onChange={(e) => onChangeNumber(e.target.value)}
        name={name}
        onKeyUp={onControlKeyUp(c, true)}
        autoFocus={autoFocus}
        placeholder="Num"
        required={required}
        error={!!error}
        style={{ flex: 1 }}
        size="small"
      />

      <Autocomplete
        disableClearable={disableClearable}
        options={list}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected}
        onChange={onChange}
        onClose={onClose}
        disabled={disabled}
        style={{ ...{ flex: 2, paddingLeft: 3 }, ...style }}
        value={{ label: splitFilterType(value?.label) } }
        ListboxProps={{
          style: {
            maxHeight: searchHeight,
          },
        }}
        PopperComponent={searchWidth ? SearchPopper(searchWidth) : undefined}
        renderInput={(params: any) => (
          <TextField
            variant={'outlined'}
            {...params}
            ref={inputRef}
            label={label}
            name={name}
            onKeyUp={onControlKeyUp(c, true)}
            autoFocus={autoFocus}
            placeholder="Range"
            required={required}
            error={!!error}
            style={{ flex: 2 }}
            size="small"
            data-field={label}
          />
        )}
      />
    </div>,
  )
}

export function Slider(c: Control) {
  let { label, tooltip, name, required, style, min, max, step } = c.field
  const value = c.value((s) => s.value)
  const classes = rootStyle(style || { width: '100%' })()
  const disabled = c.disabled((s) => s.value)

  function onChange(e: any, newValue: any) {
    if (!e) {
      return
    }
    const n = Math.max(min, Math.min(max, parseInt(newValue, 10)))
    if (c.field.onChange) {
      c.field.onChange(n)
    }
    c.value.getState().setValue(n)
  }

  return withTooltip(
    tooltip,
    <FormControl key={name} className={classes.root} required={required}>
      {label && label.length ? <InputLabel>{label}</InputLabel> : null}

      <MSlider
        onChange={onChange}
        value={value || 0}
        valueLabelDisplay="auto"
        step={step || 1}
        min={min}
        max={max}
        disabled={disabled}
      />
    </FormControl>,
  )
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

type Item<T = any> = {
  name: string
  value: T
}

export function MultiSelect(c: Control) {
  const { tooltip, inputRef, style, placeholder, name, listLabel, autoFocus, onClose } = c.field as ListProps
  const [value, setValue, error] = c.value((s) => [s.value, s.setValue, s.error], shallow)

  const disabled = c.disabled((s) => s.value)
  const list = c.list((s) => s.value) || []
  const allSelected = list.length === value?.length

  function setValueEvent(newValue) {
    if (c.field.onChange) {
      c.field.onChange(newValue)
    }
    setValue(newValue)
  }
  const onSelectAll = () => {
    if (!allSelected) {
      setValueEvent(list)
    } else {
      setValueEvent([])
    }
  }
  const onChange = (event: UIEvent) => {
    const {
      target: { value },
    } = event

    if (value[value.length - 1] === 'all') {
      onSelectAll()
      return
    }
    const selected = Array.isArray(value) ? value : value ? [value] : []
    const newValue = list.filter((a) => selected.includes(listLabel(a)))
    setValueEvent(newValue)
  }

  const listValues = Array.isArray(value) ? value : value ? [value] : []
  const labels: any = listValues.map((a) => listLabel(a))
  const maxItemsDisplay = 3
  const valueLabel = placeholder || 'Items'

  const currentValue = labels || []
  const placeholderComponent = <div style={{ opacity: 0.5 }}> {placeholder}</div>
  const id = c.id || c.field.id
  return withTooltip(
    tooltip || (labels && labels.join(', ')),
    <Select
      multiple
      displayEmpty
      value={currentValue}
      placeholder={placeholder}
      autoFocus={autoFocus}
      disabled={disabled}
      name={name}
      id={id}
      ref={inputRef}
      onClose={onClose}
      onChange={onChange}
      variant={c.variant}
      inputProps={{
        placeholder: 'HEYYY',
      }}
      style={{
        ...{
          marginTop: c.variant ? -3 : 0,
          width: (style as any)?.width || '100%',
          height: c.variant && '37.28px',
        },
        ...style,
      }}
      required={false}
      renderValue={(a: string[]) => {
        const l = a?.length

        if (!l) {
          return placeholderComponent
        }
        if (l < maxItemsDisplay) {
          return (a || []).join(',')
        }
        return `${l} ${valueLabel} Selected`
      }}
      MenuProps={MenuProps}
    >
      <MenuItem disabled value="">
        {placeholderComponent}
      </MenuItem>
      <MenuItem value="all">
        <ListItemIcon>
          <Checkbox checked={allSelected} indeterminate={!allSelected} />
        </ListItemIcon>
        <ListItemText primary="Select All" />
      </MenuItem>
      {list.map((a: Item) => {
        const name = listLabel(a)
        return (
          <MenuItem key={name} value={name}>
            <Checkbox checked={(labels && labels.find((v) => v === name)) || false} />
            <ListItemText primary={name} />
          </MenuItem>
        )
      })}
    </Select>,
  )
}

export function worksheetTypeForm(props: any): FormApi<any> {
  const { label, items, onChange, defaultValue } = props
  const form = formApi<any>({
    values: {
      worksheetType: defaultValue,
    },
    fields: {
      worksheetType: {
        value: items,
        placeholder: label || 'Worksheet Type',
        list: items || ['PHYSICIAN', 'FACILITY'],
        listLabel: (v) => v.label,
        onChange: (v) => onChange && onChange(v),
        style: { width: '200px' },
      },
    },
  })
  return form
}

export function auditBatchForm(props: any): FormApi<any> {
  const { label, items, onChange } = props
  const form = formApi<any>({
    values: {
      batch: null,
    },
    fields: {
      batch: {
        placeholder: label,
        list: items || [],
        listLabel: (v) => v.label,
        onChange: (v) => onChange && onChange(v),
        style: { width: '200px' },
      },
    },
  })
  return form
}
